// src/pages/TimeSeries/hooks/useTimeSeriesGroups.js

import { useState, useCallback } from "react";

const createEmptyTimepoint = (index) => ({
  groupName: `time_${index}`,
  controlRiboFiles: [],
  controlRnaFiles: [],
  treatRiboFiles: [],
  treatRnaFiles: [],
});

export const useTimeSeriesGroups = (allFiles = []) => {
  const [timepoints, setTimepoints] = useState([createEmptyTimepoint(0),
                                                createEmptyTimepoint(1)]);
  const [currentMaxVisibleTimepoint, setCurrentMaxVisibleTimepoint] =
    useState(1);

  const addTimepoint = useCallback(() => {
    if (currentMaxVisibleTimepoint < 12) {
      const newIndex = currentMaxVisibleTimepoint + 1;
      setTimepoints((prev) => {
        const newTimepoints = [...prev];
        if (newTimepoints.length <= newIndex) {
          newTimepoints.push(createEmptyTimepoint(newIndex));
        }
        return newTimepoints;
      });
      setCurrentMaxVisibleTimepoint(newIndex);
    }
  }, [currentMaxVisibleTimepoint]);

  const removeTimepoint = useCallback(() => {
    if (currentMaxVisibleTimepoint > 0) {
      setCurrentMaxVisibleTimepoint((prev) => prev - 1);
    }
  }, [currentMaxVisibleTimepoint]);

  const updateTimepointName = useCallback((index, name) => {
    setTimepoints((prev) =>
      prev.map((tp, i) => (i === index ? { ...tp, groupName: name } : tp))
    );
  }, []);

  const addFilesToTimepoint = useCallback(
    (timepointIndex, seqType, condType, fileIds) => {
      setTimepoints((prev) =>
        prev.map((tp, i) => {
          if (i !== timepointIndex) {
            // Remove files from other timepoints
            return {
              ...tp,
              controlRiboFiles: tp.controlRiboFiles.filter(
                (id) => !fileIds.includes(id)
              ),
              controlRnaFiles: tp.controlRnaFiles.filter(
                (id) => !fileIds.includes(id)
              ),
              treatRiboFiles: tp.treatRiboFiles.filter(
                (id) => !fileIds.includes(id)
              ),
              treatRnaFiles: tp.treatRnaFiles.filter(
                (id) => !fileIds.includes(id)
              ),
            };
          }

          // Add files to target timepoint
          const updatedTp = { ...tp };
          const targetArray = `${condType}${
            seqType.charAt(0).toUpperCase() + seqType.slice(1)
          }Files`;
          updatedTp[targetArray] = [
            ...new Set([...updatedTp[targetArray], ...fileIds]),
          ];
          return updatedTp;
        })
      );
    },
    []
  );

  const removeFilesFromTimepoint = useCallback(
    (timepointIndex, seqType, condType, fileIds) => {
      setTimepoints((prev) =>
        prev.map((tp, i) => {
          if (i === timepointIndex) {
            const targetArray = `${condType}${
              seqType.charAt(0).toUpperCase() + seqType.slice(1)
            }Files`;
            return {
              ...tp,
              [targetArray]: tp[targetArray].filter(
                (id) => !fileIds.includes(id)
              ),
            };
          }
          return tp;
        })
      );
    },
    []
  );

  const clearTimepoint = useCallback((index) => {
    setTimepoints((prev) =>
      prev.map((tp, i) =>
        i === index
          ? {
              ...tp,
              controlRiboFiles: [],
              controlRnaFiles: [],
              treatRiboFiles: [],
              treatRnaFiles: [],
            }
          : tp
      )
    );
  }, []);

  const clearAllTimepoints = useCallback(() => {
    setTimepoints((prev) =>
      prev.map((tp) => ({
        ...tp,
        controlRiboFiles: [],
        controlRnaFiles: [],
        treatRiboFiles: [],
        treatRnaFiles: [],
      }))
    );
  }, []);

  const validateTimepoints = useCallback(() => {
    const errors = [];
    const activeTimepoints = timepoints.slice(
      0,
      currentMaxVisibleTimepoint + 1
    );

    const numTimepointsWithFiles = activeTimepoints.reduce((count, tp) => {
      const hasFiles =
        tp.controlRiboFiles.length > 0 ||
        tp.controlRnaFiles.length > 0 ||
        tp.treatRiboFiles.length > 0 ||
        tp.treatRnaFiles.length > 0;

      return hasFiles ? count + 1 : count;
    }, 0);

    if (numTimepointsWithFiles < 2) {
      errors.push("Please assign files to at least two timepoints");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [timepoints, currentMaxVisibleTimepoint]);

  const getMasterFileDict = useCallback(() => {
    const masterDict = {};

    // Helper function to get file names by IDs
    const getFileNames = (fileIds) => {
      return fileIds.map((id) => {
        const file = allFiles.find((f) => f.file_id === id);
        return file ? file.file_name : `file_${id}`;
      });
    };

    // Always create all timepoints (time_0 through time_12)
    for (let index = 0; index <= 12; index++) {
      const timepointKey = `time_${index}`;
      const timepoint = timepoints[index];

      if (timepoint && index <= currentMaxVisibleTimepoint) {
        // Use actual timepoint data if it exists and is visible
        masterDict[timepointKey] = {
          control_ribo_file_ids: timepoint.controlRiboFiles.map(String),
          control_ribo_file_names: getFileNames(timepoint.controlRiboFiles),
          control_rna_file_ids: timepoint.controlRnaFiles.map(String),
          control_rna_file_names: getFileNames(timepoint.controlRnaFiles),
          treat_ribo_file_ids: timepoint.treatRiboFiles.map(String),
          treat_ribo_file_names: getFileNames(timepoint.treatRiboFiles),
          treat_rna_file_ids: timepoint.treatRnaFiles.map(String),
          treat_rna_file_names: getFileNames(timepoint.treatRnaFiles),
          group_name: timepoint.groupName || `Time point ${index}`,
        };
      } else {
        // Create empty timepoint structure for unused timepoints
        masterDict[timepointKey] = {
          control_ribo_file_ids: [],
          control_ribo_file_names: [],
          control_rna_file_ids: [],
          control_rna_file_names: [],
          treat_ribo_file_ids: [],
          treat_ribo_file_names: [],
          treat_rna_file_ids: [],
          treat_rna_file_names: [],
          group_name: `Time point ${index}`,
        };
      }
    }

    return masterDict;
  }, [timepoints, currentMaxVisibleTimepoint, allFiles]);

  return {
    timepoints,
    currentMaxVisibleTimepoint,
    addTimepoint,
    removeTimepoint,
    updateTimepointName,
    addFilesToTimepoint,
    removeFilesFromTimepoint,
    clearTimepoint,
    clearAllTimepoints,
    validateTimepoints,
    getMasterFileDict,
    // Expose setters for short code handling
    setTimepoints,
    setCurrentMaxVisibleTimepoint,
  };
};
